import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { X, AlertCircle } from "lucide-react";
import { SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { logComposition, parseTanitaText } from "@/api/body";

type Tab = "simple" | "tanita";

export function WeeklyCheckinModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("tanita");
  const [weight, setWeight] = useState<number>(85);
  const [notes, setNotes] = useState("");
  const [tanitaText, setTanitaText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["body-history"] }),
      qc.invalidateQueries({ queryKey: ["weekly-checkin"] }),
      qc.invalidateQueries({ queryKey: ["dashboard"] }),
      qc.invalidateQueries({ queryKey: ["nutrition-weight-trend"] }),
    ]);

  const submitSimple = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (weight < 30 || weight > 250) {
      setError(t("weekly_checkin.weight_range_error"));
      return;
    }
    setSaving(true);
    try {
      await logComposition({ weight_kg: weight, notes: notes || undefined });
      await invalidate();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("weekly_checkin.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const submitTanita = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (tanitaText.trim().length < 20) {
      setError(t("weekly_checkin.tanita_too_short"));
      return;
    }
    setSaving(true);
    try {
      await parseTanitaText({ text: tanitaText });
      await invalidate();
      onSaved();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Parse failed";
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-2xl border border-cream-border bg-cream-light p-5 shadow-soft"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <SectionLabel>{t("weekly_checkin.section_label")}</SectionLabel>
            <h3 className="mt-1 font-serif text-[20px] font-medium">{t("weekly_checkin.h3")}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-taupe hover:text-navy-deep"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="mb-4 flex gap-2 border-b border-cream-border">
          <TabButton active={tab === "tanita"} onClick={() => setTab("tanita")}>
            {t("weekly_checkin.tab_tanita")}
          </TabButton>
          <TabButton active={tab === "simple"} onClick={() => setTab("simple")}>
            {t("weekly_checkin.tab_simple")}
          </TabButton>
        </div>

        {tab === "tanita" ? (
          <form onSubmit={submitTanita} className="space-y-3">
            <p className="text-[12px] text-taupe">
              {t("weekly_checkin.tanita_hint")}
            </p>
            <textarea
              value={tanitaText}
              onChange={(e) => setTanitaText(e.target.value)}
              rows={12}
              placeholder={"- Weight: 95,55 kg\n- BMI: 27,9\n- Body fat: 23,2 %\n  - Torso: 28,7 %\n  ..."}
              className="w-full rounded-md border border-cream-border bg-cream px-3 py-2 font-mono text-[12px]"
            />
            {error ? (
              <p className="flex items-center gap-1 text-[13px] text-coral-dark">
                <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
                {t("knowledge.modal_cancel")}
              </Button>
              <Button type="submit" disabled={saving || !tanitaText.trim()}>
                {saving ? t("weekly_checkin.saving", { defaultValue: "Saving…" }) : t("weekly_checkin.parse_save")}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitSimple} className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("weekly_checkin.weight_kg_label")}</label>
              <input
                type="number"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(Number(e.target.value) || 0)}
                className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[18px] metric-number"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("weekly_checkin.notes_label")}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px]"
                placeholder={t("weekly_checkin.notes_placeholder")}
              />
            </div>
            {error ? <p className="text-[13px] text-coral-dark">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
                {t("knowledge.modal_cancel")}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? t("weekly_checkin.saving", { defaultValue: "Saving…" }) : t("weekly_checkin.save_only_weight")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "border-b-2 px-3 py-2 text-[13px] font-medium transition-colors " +
        (active
          ? "border-amber-dark text-navy-deep"
          : "border-transparent text-taupe hover:text-navy-deep")
      }
    >
      {children}
    </button>
  );
}
